import Button from '@/components/Buttons/Button';
import Dialog from '@/components/Dialog/BootstrapDialog';
import { ChangePlanModal } from '@/components/Modals/ChangePlanModal';
import SimpleNotificationModal from '@/components/Modals/SimpleNotificationModal';
import Close from '@/images/CloseIcon.svg';
import ConfettiBackground from '@/images/ConfettiBackground.png';
import DiscountLabel from '@/images/DiscountLabel.svg';
import PostCheckout from '@/pages/editor/components/modals/PostCheckout';
import { useAnalytics } from '@/services/analytics';
import { Billing } from '@/services/api';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { usePrice, useProductYearlyPrice } from '@/state/products/Products';
import { PaymentProvider } from '@/state/user-settings/types';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import { StyleCollection } from '@/utils/types';
import { PlanGroup } from '@d-id/types';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { DISCOUNT, DISCOUNT_FACTOR, monthlyToYearlyCoupon } from './MonthlyToYearlyUpgradeConsts';

const styles: StyleCollection = {
    dialog: {
        width: '452px',
        borderRadius: '20px',
    },
};

const Container = styled.div`
    display: flex;
    width: 452px;
    padding: 0px 0px 40px 0px;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    border-radius: 20px;
    background:
        url(${ConfettiBackground}),
        lightgray 1.008px -0.256px / 99.666% 104.308% no-repeat;
    background-color: #fff;
`;

const Label = styled.img`
    width: 80px;
    height: 70.244px;
`;

const CloseIcon = styled.div`
    width: 100%;
    height: 47.5px;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 9.5px;
    cursor: pointer;
`;

const Title = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary.main};
    text-align: center;
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
    width: 333px;
`;

const ActionButton = styled(Button)`
    display: flex;
    width: 175px;
    padding: 10px 16px;
    justify-content: center;
    align-items: center;
    gap: 6px;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.primary.main};
`;

const PriceContainer = styled.div<{ width: string }>`
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.black[50]};
    display: flex;
    width: ${({ width }) => width};
    padding: 24px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
`;

const PriceTitle = styled(Typography)`
    color: ${({ theme }) => theme.colors.black[600]};
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.28px;
`;

const Price = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary.main};
    text-align: center;
    font-size: 24px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.48px;
`;

const OffersDiv = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    gap: 16px;
`;

const OfferContainer = styled.div`
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.primary.bgLight};
    display: flex;
    width: 372px;
    padding: 24px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
`;

const OfferTitle = styled(Typography)`
    color: ${({ theme }) => theme.colors.black[600]};
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.28px;
`;

const OldAndNewPrice = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
`;

const OldPrice = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary[500]};
    text-align: center;
    font-size: 18px;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.36px;
    text-decoration: line-through;
`;

const NewPrice = styled(Typography)`
    color: ${({ theme }) => theme.colors.primary.main};
    text-align: center;
    font-size: 24px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.48px;
`;

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export enum EPopupType {
    CONTROL = 0,
    A = 1,
    B = 2,
}

interface Props {
    show: boolean;
    setIsOpen?: (state: boolean) => void;
}

export default function PlanUpgradePopUp({ show, setIsOpen }: Props) {
    const { user } = useAuth();
    const mixpanel = useAnalytics();
    const { updateUserSettings, userSettings } = useUserSettings();
    const [isOpenPlanUpgradeModal, setIsOpenPlanUpgradeModal] = useState(show);
    const [isOpenChangeModal, setIsOpenChangeModal] = useState(false);
    const [isPostCheckoutOpen, setIsPostCheckoutOpen] = useState(false);
    const [openMobileModal, setOpenMobileModal] = useState(false);
    const newPlan = user?.planGroup!;
    const newInterval = 'year';
    const isOpen = isOpenPlanUpgradeModal || isOpenChangeModal || isPostCheckoutOpen || openMobileModal;
    const popupType: EPopupType = useFlag('monthly_to_annual_popup_type', EPopupType.CONTROL);

    useEffect(() => {
        if (show) {
            mixpanel?.track('promotion', { event: 'view' });
        }
    }, [mixpanel, show]);

    useEffect(() => {
        setIsOpenPlanUpgradeModal(show);
    }, [show]);

    useEffect(() => {
        if (isOpen && !userSettings?.messages?.monthlyToAnnualDiscountOffer?.time) {
            updateUserSettings({
                messages: { monthlyToAnnualDiscountOffer: { type: EPopupType[popupType], time: new Date().toISOString() } },
            });
        }
    }, [isOpen, userSettings?.messages?.monthlyToAnnualDiscountOffer?.time]);

    useEffect(() => {
        if (setIsOpen && !isOpen) {
            setIsOpen(false);
        }
    }, [isOpen, setIsOpen]);

    const { data: currPackage, error: priceError } = usePrice(user?.priceId as string);

    if (priceError) {
        logger.error('failed to fetch price', priceError);
    }

    const currPrice = currPackage?.unit_amount ? currPackage?.unit_amount / 100 : 0;

    const { data: packageData, error: packageError } = useProductYearlyPrice(user?.priceId as string);

    if (packageError) {
        logger.error('failed to fetch yearly package', packageError);
    }

    const newPriceId = packageData?.id as string;
    const newPrice = packageData?.unit_amount ? packageData?.unit_amount / 100 / 12 : 0;

    const handleUpgrade = () => {
        mixpanel?.track('promotion', { event: 'claim' });

        if (user?.paymentProvider === PaymentProvider.AppStore) {
            mixpanel?.track('goto-mobile-manage-account', {
                redirect: 'root',
                cause: 'mobile user',
            });
            setOpenMobileModal(true);
        } else {
            setIsOpenChangeModal(true);
            setIsOpenPlanUpgradeModal(false);
        }
    };

    const handleClose = () => {
        mixpanel?.track('promotion', { event: 'close' });
        setIsOpenPlanUpgradeModal(false);
    };

    const updateUserSettingsMessage = () => {
        updateUserSettings({
            messages: { monthlyToAnnualDiscountOffer: { seen: true } },
        });
    };

    const renderPostCheckoutModal = (requestedPlan: PlanGroup, requestedBilling: Billing, newPriceId: string) => {
        if (!isPostCheckoutOpen) return null;
        return (
            <PostCheckout
                onClose={() => {
                    setIsPostCheckoutOpen(false);
                    setIsOpenChangeModal(false);
                    setIsOpenPlanUpgradeModal(false);
                    updateUserSettingsMessage();
                }}
                requestedPlan={requestedPlan}
                requestedBilling={requestedBilling}
                requestedPriceId={newPriceId}
            />
        );
    };

    const renderChangePlanModal = () => {
        if (!isOpenChangeModal) return null;
        return (
            <>
                <ChangePlanModal
                    setIsOpen={setIsOpenChangeModal}
                    newPlan={newPlan}
                    newInterval={newInterval}
                    newPriceId={newPriceId}
                    openPostCheckout={() => setIsPostCheckoutOpen(true)}
                    couponToApply={monthlyToYearlyCoupon}
                    onClose={updateUserSettingsMessage}
                />
                <SimpleNotificationModal
                    open={openMobileModal}
                    onClose={() => {
                        setOpenMobileModal(false);
                    }}
                    content="Please use the mobile app to manage your plan."
                    primaryActionContent="Got It"
                    onPrimaryAction={() => {
                        setOpenMobileModal(false);
                    }}
                    buttonPlacement="center"
                />
            </>
        );
    };

    const priceDiv = (width: string, text: string, price: string) => {
        return (
            <PriceContainer width={width}>
                <PriceTitle>{text}</PriceTitle>
                <Price>{`$${price}/month`}</Price>
            </PriceContainer>
        );
    };

    const getPricesDiv = () => {
        return popupType === (EPopupType.A as EPopupType) ? (
            <OffersDiv>
                {priceDiv('178px', 'Your current plan', `${currPrice.toFixed(2)}`)}
                {priceDiv('178px', 'Annual plan', `${newPrice.toFixed(2)}`)}
            </OffersDiv>
        ) : popupType === (EPopupType.B as EPopupType) ? (
            <>{priceDiv('364px', 'Your current plan', `${currPrice.toFixed(2)}`)}</>
        ) : (
            <></>
        );
    };

    const getOfferDiv = () => {
        return (
            <OfferContainer>
                <TextContainer>
                    <OfferTitle>Your exclusive annual offer</OfferTitle>
                    <OldAndNewPrice>
                        <OldPrice>
                            {popupType === (EPopupType.A as EPopupType)
                                ? `$${newPrice.toFixed(2)}`
                                : popupType === (EPopupType.B as EPopupType)
                                  ? `$${currPrice.toFixed(2)}`
                                  : ''}
                        </OldPrice>
                        <NewPrice>{`$${(newPrice * DISCOUNT_FACTOR).toFixed(2)}`}/month</NewPrice>
                    </OldAndNewPrice>
                </TextContainer>

                <ActionButton variant="contained" onClick={handleUpgrade}>
                    Claim offer
                </ActionButton>
            </OfferContainer>
        );
    };

    const renderUpgradeYearlyModal = () => {
        return (
            <Container>
                <CloseIcon onClick={handleClose}>
                    <img src={Close} />
                </CloseIcon>
                <Label src={DiscountLabel} />
                <Title>
                    <pre>{`Get (an extra) ${DISCOUNT}% off \n the annual plan`}</pre>
                </Title>

                {getPricesDiv()}
                {getOfferDiv()}
            </Container>
        );
    };

    if (!show) {
        return null;
    }

    if (!user?.priceId || !user?.billingInterval) {
        logger.warn('user price id or interval is missing');
        return null;
    }

    return (
        <Dialog open={isOpen} width="50rem" rootstyle={styles.dialog} contentstyle={styles.dialogContent} TransitionComponent={Transition}>
            {isOpenPlanUpgradeModal && renderUpgradeYearlyModal()}
            {renderChangePlanModal()}
            {renderPostCheckoutModal(newPlan as PlanGroup, newInterval as Billing, newPriceId)}
        </Dialog>
    );
}
